import React, { useEffect } from "react";
import { useCart } from "@/lib/cart";
import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";

export default function CartPage() {
  const { items, subtotal, setOpen } = useCart();

  useEffect(() => {
    // Auto-open the drawer to encourage checkout flow
    setOpen(true);
  }, [setOpen]);

  const shipping = subtotal >= 75 || subtotal === 0 ? 0 : 8.95;
  const total = subtotal + shipping;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="cart-page">
      <p className="b2t-overline mb-2">Your Bag</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold text-navy mb-10">Review your selection</h1>

      {items.length === 0 ? (
        <div className="bg-sand py-20 px-6 text-center">
          <ShoppingBag className="w-12 h-12 text-navy/30 mx-auto mb-4" strokeWidth={1.2} />
          <p className="text-navy/60 mb-6">Your bag is empty. The tides are calling.</p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] hover:bg-seafoam hover:text-navy transition-colors"
          >
            Shop the Collection
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-2 space-y-4">
            {items.map((i) => (
              <div key={i.key} className="flex gap-6 bg-white border border-navy/10 p-5" data-testid={`cart-page-item-${i.slug}`}>
                <div className="w-24 h-28 bg-sand overflow-hidden">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-semibold text-navy">{i.name}</h3>
                  <p className="text-xs text-navy/50 uppercase tracking-widest mt-1">
                    {i.color}{i.size ? ` · ${i.size}` : ""} · Qty {i.quantity}
                  </p>
                  <p className="mt-3 font-bold text-navy">${(i.price * i.quantity).toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>

          <aside className="bg-sand p-7 h-fit">
            <h2 className="font-display text-xl font-bold text-navy mb-5 uppercase tracking-wider">Order Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-navy/60">Subtotal</span>
                <span className="font-semibold text-navy">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-navy/60">Shipping</span>
                <span className="font-semibold text-navy">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-navy/10 text-base">
                <span className="font-display font-bold text-navy">Total</span>
                <span className="font-display font-bold text-navy">${total.toFixed(2)}</span>
              </div>
            </div>
            <button
              onClick={() => setOpen(true)}
              data-testid="cart-page-checkout-button"
              className="mt-6 w-full bg-navy text-white py-4 text-xs font-bold uppercase tracking-[0.22em] hover:bg-seafoam hover:text-navy transition-colors flex items-center justify-center gap-2"
            >
              Checkout
              <ArrowRight className="w-4 h-4" />
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
